// MARK://dhk8/pos/lib/accent-ramp
// Runtime accent-ramp engine for the POS shell.
//
// Input: one base accent ("tint") color — any CSS color string the browser can
// parse (hex `#1F8B3A`, `oklch(...)`, `rgb(...)`, named colors…). Default is the
// NintaiGO HIG-green.
//
// Output: a flat `{ '--pos-…': value }` map covering every accent-derived token
// the POS uses, plus the legacy `--brand-*` aliases that ported sc-restaurant
// components still read. The branding-loader applies it to `:root` via
// `root.style.setProperty(...)`, so per-property overrides take precedence over
// the static `tokens.css` / `nintaigo-brand.css` defaults without a rebuild.
//
// We don't parse the color ourselves — we derive the ramp with CSS
// `color-mix(in oklch, …)` (already used throughout tokens.css). The browser
// does the oklch math; we just describe "X% accent mixed toward white/black/
// transparent". A "strong" (darker, for ink/active) and a contrasting "on" fg
// (a light tint of the accent, readable on the dark fill) are derived the same
// way. This mirrors how the static green tokens are built.
//
// Tokens covered (all keys returned even when the accent === default green):
//   --pos-tint              the accent itself
//   --pos-tint-strong       darker accent (ink / pressed)         — mix toward black
//   --pos-tint-soft         12% accent on transparent             — subtle bg wash
//   --pos-tint-hover        18% accent on transparent             — hover bg wash
//   --pos-tint-fill         22% accent on transparent             — selected nav fill
//   --pos-tint-fill-hover   30% accent on transparent             — selected nav hover
//   --pos-tint-on           light tint of accent                  — fg ON the fill
//   --pos-on-tint           text/icon ON the solid accent CTA     — white | near-black
//   --brand-green           legacy alias  == --pos-tint
//   --brand-deep            legacy alias  == --pos-tint-strong
//   --brand-leaf            legacy alias  == --pos-tint-on
//   --brand-mid             legacy alias  == lighter accent (gradient mid stop)
//   --brand-grad            recomputed 2-stop CTA gradient (mid → deep)
//   --brand-grad-leaf       recomputed 3-stop glyph gradient (leaf → green → deep)

export const DEFAULT_ACCENT = '#1F8B3A' // == --hig-green-600, the NintaiGO HIG-green

/** Stable list of CSS custom properties this engine writes. Useful for cleanup. */
export const ACCENT_TOKEN_KEYS = [
  '--pos-tint',
  '--pos-tint-strong',
  '--pos-tint-soft',
  '--pos-tint-hover',
  '--pos-tint-fill',
  '--pos-tint-fill-hover',
  '--pos-tint-on',
  '--pos-on-tint',
  '--brand-green',
  '--brand-deep',
  '--brand-leaf',
  '--brand-mid',
  '--brand-grad',
  '--brand-grad-leaf',
] as const

export type AccentTokenKey = (typeof ACCENT_TOKEN_KEYS)[number]
export type AccentRamp = Record<AccentTokenKey, string>

const mix = (a: string, pct: number, b: string): string => `color-mix(in oklch, ${a} ${pct}%, ${b})`

/**
 * Rough perceived-lightness probe so the "on accent" foreground (the text drawn
 * on the solid CTA) stays legible. We only have a string here, so we parse the
 * common cases (`#rgb`, `#rrggbb`, `rgb()/rgba()`) and fall back to "assume
 * mid-dark" (→ white text) for anything fancier (`oklch()`, named colors). The
 * brand greens and every preset swatch we ship are mid-to-dark, so white wins.
 */
function isLightColor(c: string): boolean {
  const s = c.trim().toLowerCase()
  let r: number | undefined
  let g: number | undefined
  let b: number | undefined
  const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/)
  if (hex) {
    const h = hex[1]
    if (h.length === 3) {
      r = parseInt(h[0] + h[0], 16)
      g = parseInt(h[1] + h[1], 16)
      b = parseInt(h[2] + h[2], 16)
    } else {
      r = parseInt(h.slice(0, 2), 16)
      g = parseInt(h.slice(2, 4), 16)
      b = parseInt(h.slice(4, 6), 16)
    }
  } else {
    const rgb = s.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/)
    if (rgb) {
      r = Number(rgb[1])
      g = Number(rgb[2])
      b = Number(rgb[3])
    }
  }
  if (r === undefined || g === undefined || b === undefined) return false
  // Rec. 601 luma, 0–255.
  const luma = 0.299 * r + 0.587 * g + 0.114 * b
  return luma > 150
}

/**
 * Build the full accent ramp from a single base color.
 *
 * @param accent base accent color (any CSS color). Falsy / blank → DEFAULT_ACCENT.
 */
export function buildAccentRamp(accent?: string | null): AccentRamp {
  const base = accent && accent.trim() ? accent.trim() : DEFAULT_ACCENT
  const black = '#000'
  const white = '#fff'

  const strong = mix(base, 70, black) // darker ink-accent
  const deep = mix(base, 55, black) // even darker — bottom gradient stop
  const mid = mix(base, 88, white) // lighter — mid gradient stop
  const leaf = mix(base, 70, white) // light tint — "GO" tail / fg on fill
  const onTint = isLightColor(base) ? '#0E0E12' : white

  return {
    '--pos-tint': base,
    '--pos-tint-strong': strong,
    '--pos-tint-soft': mix(base, 12, 'transparent'),
    '--pos-tint-hover': mix(base, 18, 'transparent'),
    '--pos-tint-fill': mix(base, 22, 'transparent'),
    '--pos-tint-fill-hover': mix(base, 30, 'transparent'),
    '--pos-tint-on': leaf,
    '--pos-on-tint': onTint,
    '--brand-green': base,
    '--brand-deep': deep,
    '--brand-leaf': leaf,
    '--brand-mid': mid,
    '--brand-grad': `linear-gradient(180deg, ${mid} 0%, ${deep} 100%)`,
    '--brand-grad-leaf': `linear-gradient(180deg, ${leaf} 0%, ${base} 55%, ${deep} 100%)`,
  }
}

/** Apply a ramp (or any token map) to a DOM element — defaults to `:root`. */
export function applyAccentRamp(ramp: Record<string, string>, el?: HTMLElement): void {
  if (typeof document === 'undefined') return
  const root = el ?? document.documentElement
  for (const [k, v] of Object.entries(ramp)) root.style.setProperty(k, v)
}
